import { Component, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';

import { Auction } from '../../../auction/auction.model';
import { LotService } from '../../../lots/lots.service';
import { BID_ORIGIN_LABELS, BidLot, LotInfo } from '../../../panel/panel.model';
import { PanelService } from '../../../panel/panel.service';
import { AppParamItems, AppParamsService } from '../../app-params.service';
import { CardTemplateConfig } from '../card-template/card-template.component';
import { CurrencyType } from '../../currency/currency-types';
import { DialogService } from '../../dialog/dialog.service';
import { ExchangeMoneyFormatter, ExchangeRates } from '../../exchange-formatter/exchange-formatter';
import { NotificationService } from '../../notification.service';

const BIDS_LIMIT = 5;

@Component({
  selector: 'app-auction-recent-bids',
  templateUrl: './auction-recent-bids.component.html',
  styleUrls: ['./auction-recent-bids.component.scss'],
})
export class AuctionRecentBidsComponent implements OnDestroy, OnChanges {
  @Input() canDeleteBids = false;
  @Input() currentAuction: Auction | null = null;
  @Input() enableBidManagement = false;
  @Input() lotInfo: LotInfo | null = null;
  @Input() large = false;

  @HostBinding('class.auction-recent-bids--large')
  get isLarge(): boolean {
    return this.large;
  }

  @Output() readonly bidArrived = new EventEmitter<BidLot>();
  @Output() readonly highestBidChange = new EventEmitter<BidLot | null>();

  public readonly bids$ = new BehaviorSubject<BidLot[]>([]);
  public readonly error$ = new BehaviorSubject<string | null>(null);
  public readonly hasBids$ = this.bids$.pipe(map((bids) => bids.length > 0));
  public readonly isLoading$ = new BehaviorSubject<boolean>(false);
  public exchangeRates: ExchangeRates[] = [];
  public readonly bidOriginLabels = BID_ORIGIN_LABELS;

  private get cardConfig(): CardTemplateConfig {
    return {
      title: 'Últimas 5 pujas',
      subtitle: '',
      showSubtitle: true,
      cardClass: this.large ? 'bids-card fill-card' : 'bids-card',
      compact: true,
    };
  }

  private readonly destroy$ = new Subject<void>();
  private bidsSubscription?: Subscription;
  private exchangeRatesSubscription = new Subscription();
  private firstBidsLoad = true;
  private lastBidTime: number | null = null;

  constructor(
    private readonly dialog: DialogService,
    private readonly exchangeMoneyFormatter: ExchangeMoneyFormatter,
    private readonly lotService: LotService,
    private readonly notificationService: NotificationService,
    private readonly panelService: PanelService,
    private readonly params: AppParamsService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentAuction']) {
      this.setupExchangeRatesWatch();
    }
    if (changes['currentAuction'] || changes['lotInfo']) {
      this.subscribeToBids();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribeFromBids();
    this.exchangeRatesSubscription.unsubscribe();
    void Promise.all([
      this.params.dispose(AppParamItems.AppParamGeneral.primaryCurrencyType),
      this.params.dispose(AppParamItems.AppParamGeneral.exchangeRate),
    ]);
    this.destroy$.next();
    this.destroy$.complete();
  }

  get cardTemplateConfig(): CardTemplateConfig {
    return this.cardConfig;
  }

  isWinningBid(index: number): boolean {
    return this.isLotFinalized && index === 0;
  }

  onDeleteAllBids(): void {
    this.dialog
      .askWithSecurePhrase('Esta seguro de eliminar las pujas del lote actual?', 'Escriba la palabra: borrar', 'borrar')
      .afterClosed()
      .pipe(filter((confirmed) => confirmed), first())
      .subscribe(() => this.confirmDeleteAllBids());
  }

  onDeleteLastBid(): void {
    const lotCode = this.lotInfo?.code;
    if (!lotCode) {
      return;
    }

    this.dialog
      .askBinary(`Esta seguro de eliminar la ultima puja del lote ${lotCode} ?`)
      .afterClosed()
      .pipe(filter((confirmed) => confirmed), first())
      .subscribe(() => this.confirmDeleteLastBid(lotCode));
  }

  private confirmDeleteAllBids(): void {
    const auctionId = this.currentAuction?.id;
    const lotCode = this.lotInfo?.code;
    if (!auctionId || !lotCode) {
      return;
    }

    this.lotService
      .deleteLotBids(lotCode, auctionId)
      .then(() => this.notificationService.success(`Pujas del lote ${lotCode} han sido eliminadas`))
      .catch((err) => this.notificationService.error(err));
  }

  private get isLotFinalized(): boolean {
    return !!this.lotInfo?.code && !this.lotInfo.active;
  }

  private confirmDeleteLastBid(lotCode: string): void {
    const auctionId = this.currentAuction?.id;
    if (!auctionId) {
      return;
    }

    this.panelService
      .deleteLastBid(auctionId, lotCode)
      .catch((err) => this.notificationService.error(err));
  }

  private handleBidsUpdate(bids: BidLot[]): void {
    this.bids$.next(bids);
    this.highestBidChange.emit(bids[0] ?? null);

    if (bids.length === 0) {
      this.firstBidsLoad = false;
      return;
    }

    const currentBidTime = new Date(bids[0].date).getTime();
    if (Number.isNaN(currentBidTime)) {
      this.firstBidsLoad = false;
      return;
    }

    if (!this.firstBidsLoad && (this.lastBidTime === null || currentBidTime > this.lastBidTime)) {
      this.bidArrived.emit(bids[0]);
    }

    this.lastBidTime = currentBidTime;
    this.firstBidsLoad = false;
  }

  private subscribeToBids(): void {
    this.unsubscribeFromBids();

    const auctionId = this.currentAuction?.id;
    const lotCode = this.lotInfo?.code;
    if (!auctionId || !lotCode) {
      this.bids$.next([]);
      this.highestBidChange.emit(null);
      this.firstBidsLoad = true;
      this.lastBidTime = null;
      return;
    }

    this.isLoading$.next(true);
    this.error$.next(null);
    this.firstBidsLoad = true;
    this.lastBidTime = null;

    this.bidsSubscription = this.panelService.getLotBids(auctionId, lotCode, BIDS_LIMIT).subscribe({
      next: (bids) => {
        this.handleBidsUpdate(bids);
        this.isLoading$.next(false);
      },
      error: (error) => {
        this.error$.next('Error loading bids');
        console.error('Error loading bids:', error);
        this.isLoading$.next(false);
      },
    });
  }

  private setupExchangeRatesWatch(): void {
    this.exchangeRatesSubscription.unsubscribe();
    this.exchangeRatesSubscription = new Subscription();

    const auctionId = this.currentAuction?.id;
    if (!auctionId) {
      this.exchangeRates = [];
      void Promise.all([
        this.params.dispose(AppParamItems.AppParamGeneral.primaryCurrencyType),
        this.params.dispose(AppParamItems.AppParamGeneral.exchangeRate),
      ]);
      return;
    }

    void Promise.all([
      this.params.dispose(AppParamItems.AppParamGeneral.primaryCurrencyType),
      this.params.dispose(AppParamItems.AppParamGeneral.exchangeRate),
    ]).then(() => {
      const primarySub = this.params
        .watchFor(auctionId, AppParamItems.AppParamGeneral.primaryCurrencyType)
        .subscribe((primaryCurrencyType: CurrencyType) => {
          const format = this.exchangeMoneyFormatter.getFormat(primaryCurrencyType);
          if (format.requiresMultiMoney) {
            void this.params.dispose(AppParamItems.AppParamGeneral.exchangeRate).then(() => {
              const rateSub = this.params
                .watchFor(auctionId, AppParamItems.AppParamGeneral.exchangeRate)
                .subscribe((exchangeRate) => {
                  this.exchangeRates = [
                    { currencyCode: primaryCurrencyType, exchangeRate: 1 },
                    { currencyCode: CurrencyType.USD, exchangeRate: parseFloat(String(exchangeRate)) || 0 },
                  ];
                });
              this.exchangeRatesSubscription.add(rateSub);
            });
          } else {
            this.exchangeRates = [{ currencyCode: CurrencyType.USD, exchangeRate: 1 }];
          }
        });
      this.exchangeRatesSubscription.add(primarySub);
    });
  }

  private unsubscribeFromBids(): void {
    if (this.bidsSubscription) {
      this.bidsSubscription.unsubscribe();
      this.bidsSubscription = undefined;
    }
  }
}
